/**
 * «Today» for a show, in ONE place. Owner, 30.10.2026: «считать по UTC а отображать по таймзоне клиента / шоу».
 *
 * A MOMENT (sent, saved, a tick, a workday start) is a UTC instant and is shown in the viewer's own zone. A show's DAY is not a moment:
 * it is a date on the wall of the PLACE (`venues.time_zone`; a show with no venue gets the venue default). So a show is «past» when the
 * current instant is after the END of its last day in that zone - the same answer for a viewer anywhere, which is why the server can
 * give it without asking the browser anything.
 *
 * Pure and engine-free (`Intl` only): the API (the Past pill, the past-date guard) and the web (the picker, the lists) both import it,
 * so there is no second copy of the rule.
 */
import { candidateDays } from "./show";
/** The zone a show's day is read in when it has no venue, and the one every venue gets (`venues.time_zone` default). */
export const DEFAULT_SHOW_ZONE = "Asia/Jerusalem";
const formatters = new Map();
function formatterOf(zone) {
    const known = formatters.get(zone);
    if (known)
        return known;
    if (typeof zone !== "string" || zone.trim() === "" || zone.length > 64)
        return null;
    try {
        const made = new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
        formatters.set(zone, made);
        return made;
    }
    catch {
        return null;
    }
}
/** Whether `zone` is an IANA zone this runtime knows. */
export function isTimeZone(zone) {
    return typeof zone === "string" && formatterOf(zone) !== null;
}
/** The zone itself when it is one, else the venue default - garbage never throws and never moves a day by accident. */
export function resolveZone(zone) {
    return isTimeZone(zone) ? zone : DEFAULT_SHOW_ZONE;
}
/** The date (`YYYY-MM-DD`) on the wall of `zone` at the instant `now`. */
export function todayIn(zone, now = new Date()) {
    return formatterOf(resolveZone(zone)).format(now);
}
/** Whether the day `date` (`YYYY-MM-DD`, a day OF that zone) has ended there: the instant is after its last moment, i.e. the zone's today is later. */
export function isPastIn(date, zone, now = new Date()) {
    return date < todayIn(zone, now);
}
/**
 * The days a date asks for that are already over in `zone`: a day; every day of a choice; the FIRST day of a window (the day the screen
 * offers); an open date asks for none. `already` is the date the show had before: a day it already had is left out, so moving a date INTO
 * the past is refused while saving the title of a show that has long since played is not.
 */
export function pastDaysOf(when, zone, now = new Date(), already) {
    const asked = when.kind === "window" ? [when.from] : when.kind === "day" ? [when.date] : when.kind === "choice" ? [...new Set(when.dates)].sort() : [];
    const had = new Set(already ? (already.kind === "window" ? [already.from, ...candidateDays(already)] : candidateDays(already)) : []);
    const today = todayIn(zone, now);
    return asked.filter((d) => d < today && !had.has(d));
}
//# sourceMappingURL=zone-day.js.map